// API client for Xpanda Market backend
// Используем относительный путь для production (один домен), полный URL для локальной разработки
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

import { setMaintenance } from './maintenanceState';

// ─── Проактивный silent refresh ──────────────────────────────────────────────
// Вместо реактивного (только по 401) ставим таймер за 60s до истечения
// access-токена. Это решает две проблемы:
// 1. Multi-tab race: вкладка A обновила токены раньше — вкладка B узнаёт
//    об этом через storage-event и не пытается refresh своим устаревшим токеном.
// 2. Сетевая ошибка при refresh: не логаутим сразу, делаем retry через 15s.

let _refreshTimer: ReturnType<typeof setTimeout> | null = null;

// Тип результата попытки обновить токен.
type RefreshResult = 'ok' | 'invalid' | 'network_error';
// Singleton in-flight promise — объявляем здесь чтобы storage-listener мог
// к нему обращаться без hoisting-проблем.
let _refreshInFlight: Promise<RefreshResult> | null = null;

/** Распарсить exp из JWT без верификации подписи (только для планирования таймера). */
function _parseJwtExp(token: string): number | null {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Запланировать проактивный refresh за 60s до истечения access-токена. */
function _scheduleRefresh(accessToken: string): void {
  if (_refreshTimer !== null) {
    clearTimeout(_refreshTimer);
    _refreshTimer = null;
  }
  const exp = _parseJwtExp(accessToken);
  if (!exp) return;
  const delay = exp * 1000 - Date.now() - 60_000; // за 60s до истечения
  if (delay <= 0) {
    // Токен уже истёк или вот-вот — refresh немедленно
    void _silentRefresh();
  } else {
    _refreshTimer = setTimeout(() => void _silentRefresh(), delay);
  }
}

/** Тихий фоновый refresh: retry при сетевой ошибке, logout только при невалидном токене. */
async function _silentRefresh(): Promise<void> {
  const result = await refreshToken();
  if (result === 'network_error') {
    // Сеть недоступна — повторить через 15s, не трогать токены
    _refreshTimer = setTimeout(() => void _silentRefresh(), 15_000);
  } else if (result === 'invalid') {
    // Refresh-токен невалиден (истёк или отозван) — разлогинить
    clearTokens();
    emitAuthLogout();
  }
  // 'ok' — setTokens уже вызван внутри refreshToken, там же сработал _scheduleRefresh
}

// ─── Cross-tab sync через storage events ────────────────────────────────────
// Если другая вкладка обновила токены — переиспользуем их, не делая повторный
// refresh. Если другая вкладка разлогинилась — синхронно разлогиниться и здесь.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === 'access_token') {
      if (e.newValue) {
        // Другая вкладка успешно обновила access-токен
        _refreshInFlight = null; // сбросить in-flight если был
        _scheduleRefresh(e.newValue);
      } else {
        // Другая вкладка удалила токен (logout)
        if (_refreshTimer !== null) { clearTimeout(_refreshTimer); _refreshTimer = null; }
        emitAuthLogout();
      }
    }
  });
}

// ─── Token storage ───────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem('access_token');
const setTokens = (access: string, refresh: string) => {
  localStorage.setItem('access_token', access);
  localStorage.setItem('refresh_token', refresh);
  _scheduleRefresh(access); // запустить/перезапустить таймер проактивного refresh
};
const clearTokens = () => {
  if (_refreshTimer !== null) { clearTimeout(_refreshTimer); _refreshTimer = null; }
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

// Инициализация таймера при загрузке страницы — если токен уже есть в
// localStorage (пользователь обновил страницу или открыл новую вкладку).
// Без этого таймер запустится только при следующем setTokens (после refresh),
// и окно между загрузкой и первым refresh'ом никак не покрыто.
if (typeof window !== 'undefined') {
  const _existingToken = localStorage.getItem('access_token');
  if (_existingToken) _scheduleRefresh(_existingToken);
}

// Request helper with auth
async function apiRequest(
  endpoint: string,
  options: RequestInit = {}
): Promise<any> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };
  
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    if (response.status === 503) {
      // Maintenance mode middleware ответил — взводим global-флаг,
      // App.tsx переключит UI на MaintenancePage.
      const body = await response.clone().json().catch(() => null);
      if (body && body.detail === 'maintenance_mode') {
        setMaintenance(true, body.message || '');
        throw new Error('maintenance_mode');
      }
    }

    if (response.status === 401) {
      // Try to refresh token (deduplicated через _refreshInFlight).
      const refreshResult = await refreshToken();
      if (refreshResult === 'ok') {
        return apiRequest(endpoint, options);
      } else if (refreshResult === 'network_error') {
        // Сетевая ошибка во время refresh — токены валидны, просто нет сети
        throw new Error('Нет соединения с сервером, попробуйте ещё раз');
      } else {
        // 'invalid' — refresh-токен невалиден, разлогинить
        clearTokens();
        // Эмитим event ДО redirect'а — AuthContext сразу очищает state,
        // Header убирает аватар. Раньше state жил до полной перезагрузки.
        emitAuthLogout();
        // Не редиректим если уже на /login или на главной (SPA-route).
        const path = window.location.pathname;
        if (path !== '/login' && path !== '/') {
          window.location.href = '/login';
        }
        throw new Error('Session expired');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      // detail может быть строкой ИЛИ объектом (когда backend проксирует
      // nested error от pc-1 типа {detail: {detail: "..."}}). Достаём
      // строку из любой глубины.
      let msg = error.detail ?? `HTTP ${response.status}`;
      while (msg && typeof msg === 'object' && 'detail' in msg) msg = msg.detail;
      if (typeof msg !== 'string') {
        try { msg = JSON.stringify(msg); } catch { msg = String(msg); }
      }
      throw new Error(msg);
    }
    
    const data = await response.json();
    // Debug: log purchase/deposit responses
    if (endpoint.includes('/purchases') || endpoint.includes('/deposits')) {
      console.log('[API] Response:', endpoint, data);
    }
    return data;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

// Singleton-promise защищающий от refresh-race: backend ротирует refresh_token
// на каждом обмене, значит если параллельные 401-запросы оба попытаются
// refresh — первый ротирует, второй с устаревшим refresh получит fail и
// юзера выкинет. Все одновременные refresh'ы ждут одной операции.
//
// Тип RefreshResult и _refreshInFlight объявлены выше (до storage-listener)
// чтобы избежать TDZ-проблем при обращении из callback'а.
async function refreshToken(): Promise<RefreshResult> {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async (): Promise<RefreshResult> => {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) return 'invalid';
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (response.ok) {
        const data = await response.json();
        setTokens(data.access_token, data.refresh_token);
        return 'ok';
      }
      // 401/403/400 — токен невалиден
      return 'invalid';
    } catch (error) {
      // Сетевая ошибка — не инвалидируем токены
      console.error('Token refresh failed (network):', error);
      return 'network_error';
    }
  })();
  try {
    return await _refreshInFlight;
  } finally {
    _refreshInFlight = null;
  }
}

// Event для немедленного оповещения UI о потере сессии — иначе AuthContext
// продолжает считать юзера залогиненным до полной перезагрузки.
const AUTH_LOGOUT_EVENT = 'xpanda:auth-logout';

export function emitAuthLogout() {
  try { window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT)); } catch {}
}

export function onAuthLogout(cb: () => void): () => void {
  window.addEventListener(AUTH_LOGOUT_EVENT, cb);
  return () => window.removeEventListener(AUTH_LOGOUT_EVENT, cb);
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth API
// ─────────────────────────────────────────────────────────────────────────────

// Публичный read-only флаг maintenance — используется App.tsx ДО
// рендера маркета, чтобы не было мигания. Делаем raw fetch (а не
// apiRequest), иначе maintenanceState set'нется через 503-перехватчик
// и поведение запутается; здесь это просто decisional data.
export const getPublicMaintenanceStatus = async (): Promise<{
  enabled: boolean;
  message: string;
}> => {
  try {
    const r = await fetch(`${API_BASE_URL}/maintenance/status`);
    if (!r.ok) return { enabled: false, message: '' };
    const data = await r.json();
    return {
      enabled: !!data.enabled,
      message: typeof data.message === 'string' ? data.message : '',
    };
  } catch {
    // Network error — fail-open (показываем market, иначе деградирует UX).
    return { enabled: false, message: '' };
  }
};

export const authApi = {
  getSteamLoginUrl: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/steam`);
    if (!response.ok) {
      throw new Error('Failed to get Steam login URL');
    }
    return response.json();
  },

  // Обмен одноразового auth_code (выданного Steam-callback'ом в URL) на JWT-пару.
  // Code single-use, TTL 60с — после этого backend вернёт 401.
  exchangeAuthCode: async (code: string): Promise<{ access_token: string; refresh_token: string }> => {
    const response = await fetch(`${API_BASE_URL}/auth/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!response.ok) {
      throw new Error(`Auth code exchange failed: HTTP ${response.status}`);
    }
    return response.json();
  },

  logout: async () => {
    await apiRequest('/auth/logout', { method: 'POST' });
    clearTokens();
  },

  isAuthenticated: () => !!getToken(),

  setTokens,
  clearTokens,
  getToken,
};

// ─────────────────────────────────────────────────────────────────────────────
// Profile API
// ─────────────────────────────────────────────────────────────────────────────

export const profileApi = {
  getProfile: () => apiRequest('/profile'),
  
  updateProfile: (data: { steam_trade_url?: string; phone?: string; email?: string }) => 
    apiRequest('/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  getTransactions: (limit = 50) => 
    apiRequest(`/profile/transactions?limit=${limit}`),
};

// ─────────────────────────────────────────────────────────────────────────────
// Market API
// ─────────────────────────────────────────────────────────────────────────────

export interface ItemsFilter {
  page?: number;
  limit?: number;
  search?: string;
  min_price?: number;
  max_price?: number;
  weapons?: string;  // Comma-separated list
  rarities?: string; // Comma-separated list
  qualities?: string; // Comma-separated list
  statTrak?: boolean;
  // Фильтры по данным расширенного прайса (/v1/items/full-prices/)
  categories?: string; // normal,stattrak,souvenir,star
  phases?: string;     // Phase 1..4, Emerald, Sapphire, Ruby, Black Pearl
  has_sticker?: boolean;
  has_keychain?: boolean;
  delivery?: string;  // fast | slow
  float_min?: number;
  float_max?: number;
  sort?: 'popular' | 'price_asc' | 'price_desc' | 'discount';
}

export const marketApi = {
  getItems: (filters: ItemsFilter = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    return apiRequest(`/items?${params.toString()}`);
  },
  
  getItemDetail: (itemId: number) => apiRequest(`/items/${itemId}`),
  
  getItemFreshPrice: (itemId: number) => apiRequest(`/items/${itemId}/price`),
  
  getMarketStats: () => apiRequest('/market/stats'),
  
  // Product Card API (SEO-optimized)
  getProductCard: (slug: string, params?: string) => 
    apiRequest(`/market/product/${slug}${params ? '?' + params : ''}`),
  getListingDetail: (listingId: number) => 
    apiRequest(`/market/listing/${listingId}`),
  searchProducts: (query: string) => 
    apiRequest(`/market/search?q=${encodeURIComponent(query)}`),
  getPopularProducts: () => 
    apiRequest('/market/popular'),
  getCategories: () => 
    apiRequest('/market/categories'),
};

// ─────────────────────────────────────────────────────────────────────────────
// Purchases API
// ─────────────────────────────────────────────────────────────────────────────

export const purchaseApi = {
  createPurchase: (itemId: number, paymentMethod: 'invizo' | 'balance' = 'invizo', phone?: string, email?: string, save_contacts?: boolean) =>
    apiRequest('/purchases', {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId, payment_method: paymentMethod, phone, email, save_contacts }),
    }),

  confirmPurchase: (transactionId: number) =>
    apiRequest(`/purchases/${transactionId}/confirm`, {
      method: 'POST',
    }),

  getStatus: (transactionId: number): Promise<OrderStatus> =>
    apiRequest(`/purchases/${transactionId}/status`),
};

// ─────────────────────────────────────────────────────────────────────────────
// Balance API
// ─────────────────────────────────────────────────────────────────────────────

export const balanceApi = {
  getBalance: () => apiRequest('/balance'),
  
  createDeposit: (amount: number, paymentMethod: 'invizo' = 'invizo', phone?: string, email?: string) =>
    apiRequest('/deposits', {
      method: 'POST',
      body: JSON.stringify({ amount, payment_method: paymentMethod, phone, email }),
    }),
  
  checkDepositStatus: (transactionId: number) =>
    apiRequest(`/deposits/${transactionId}/status`),
};

// ─────────────────────────────────────────────────────────────────────────────
// Payments API
// ─────────────────────────────────────────────────────────────────────────────

export const paymentsApi = {
  checkInvizoStatus: (number: string) =>
    apiRequest(`/payments/invizo/check?number=${encodeURIComponent(number)}`),
};

// Export types
export interface Profile {
  id: number;
  steam_id: string;
  steam_name: string;
  steam_avatar: string;
  steam_profile_url: string;
  steam_trade_url: string;
  // null когда pc недоступен (fail-soft); UI показывает «—»
  balance: number | null;
  referral_code: string;
  referrals: number;
  has_gift: boolean;
  phone?: string;
  email?: string;
  is_admin?: boolean;
  is_super_admin?: boolean;
  // UUID пользователя в pc.admins (для 4-eyes: «эта pending action создана мной»)
  pc_admin_id?: string | null;
  created_at: string;
}

export interface Transaction {
  id: number;
  type: 'deposit' | 'purchase' | 'sale' | 'refund' | 'gift';
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'cancelled' | 'failed' | 'paid';
  item_name?: string;
  item_image?: string;
  payment_method?: string;
  created_at: string;
  completed_at?: string;
  // Canonical UI-статус (считается на бэке, _compute_display).
  display_kind?: 'deposit' | 'purchase' | 'refund' | string;
  display_status?: string;
  display_label?: string;
  amount_sign?: '+' | '-' | '';
}

export interface OrderStatus {
  transaction_id: number;
  item_name?: string;
  item_image?: string;
  amount: number;
  currency: string;
  status: string;
  xpanda_deal_id?: string;
  xpanda_custom_id?: string;
  created_at?: string;
  completed_at?: string;
  xpanda_status?: string;
  order_status?: string;
  xpanda_status_label?: string;
  order_status_label?: string;
  steam_trade_offer_id?: string;
  error_code?: string;
  error?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin API
// ─────────────────────────────────────────────────────────────────────────────

export interface RefundRequestItem {
  id: number;
  status: 'pending' | 'approved' | 'rejected';
  user_reason: string | null;
  admin_note: string | null;
  created_at: string;
  resolved_at: string | null;
  transaction: {
    id: number;
    item_name: string | null;
    amount: number;
    payment_method: string;
    payment_id: string | null;
    status: string;
  } | null;
  user: {
    id: number;
    steam_name: string;
    email: string | null;
  } | null;
}

export const adminApi = {
  // initDb удалён — миграции теперь идут через alembic, не через HTTP.

  listRefundRequests: (status?: string) =>
    apiRequest(`/admin/refund-requests${status ? `?status=${status}` : ''}`),

  getRefundRequest: (id: number) =>
    apiRequest(`/admin/refund-requests/${id}`),

  approveRefund: (id: number, note?: string) =>
    apiRequest(`/admin/refund-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ note: note || null }),
    }),

  rejectRefund: (id: number, note?: string) =>
    apiRequest(`/admin/refund-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note: note || null }),
    }),

  searchUsers: (q: string) =>
    apiRequest(`/admin/users/search?q=${encodeURIComponent(q)}`),

  listUsers: (filters: {
    q?: string;
    adminsOnly?: boolean;
    balanceGtZero?: boolean;
    active7d?: boolean;
    new24h?: boolean;
    hasTradeUrl?: boolean;
    createdAfter?: string;
    createdBefore?: string;
    limit?: number;
  } = {}) => {
    const p = new URLSearchParams();
    if (filters.q) p.set('q', filters.q);
    if (filters.adminsOnly) p.set('admins_only', 'true');
    if (filters.balanceGtZero) p.set('balance_gt_zero', 'true');
    if (filters.active7d) p.set('active_7d', 'true');
    if (filters.new24h) p.set('new_24h', 'true');
    if (filters.hasTradeUrl) p.set('has_trade_url', 'true');
    if (filters.createdAfter) p.set('created_after', filters.createdAfter);
    if (filters.createdBefore) p.set('created_before', filters.createdBefore);
    if (filters.limit) p.set('limit', String(filters.limit));
    const qs = p.toString();
    return apiRequest(`/admin/users${qs ? `?${qs}` : ''}`);
  },

  getUserDetail: (userId: number) =>
    apiRequest(`/admin/users/${userId}`),

  listTransactions: (filters: {
    status?: string;
    type?: string;
    pageToken?: string;
    limit?: number;
  } = {}) => {
    const p = new URLSearchParams();
    if (filters.status)    p.set('status', filters.status);
    if (filters.type)      p.set('type', filters.type);
    if (filters.pageToken) p.set('page_token', filters.pageToken);
    if (filters.limit)     p.set('limit', String(filters.limit));
    const qs = p.toString();
    return apiRequest(`/admin/transactions${qs ? `?${qs}` : ''}`);
  },

  // Полная история одной транзакции (state_history + purchase embed)
  getTransactionDetail: (txId: string) =>
    apiRequest(`/admin/transactions/${txId}`),

  adjustBalance: (userId: number, amount: number, note?: string) =>
    apiRequest(`/admin/users/${userId}/adjust-balance`, {
      method: 'POST',
      body: JSON.stringify({ amount, note: note || null }),
    }),

  createRefundRequest: (transactionId: number, reason?: string) =>
    apiRequest(`/admin/transactions/${transactionId}/refund-request`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || null }),
    }),

  listAudit: (afterId = 0, limit = 50) =>
    apiRequest(`/admin/audit?after_id=${afterId}&limit=${limit}`),

  getMaintenance: () => apiRequest(`/admin/maintenance`),

  setMaintenance: (enabled: boolean, message = '') =>
    apiRequest(`/admin/maintenance`, {
      method: 'POST',
      body: JSON.stringify({ enabled, message }),
    }),

  // ── Admin management (super_admin only) ─────────────────────────────
  listAdmins: (includeInactive = false) =>
    apiRequest(
      `/admin/admins${includeInactive ? '?include_inactive=true' : ''}`,
    ),

  promoteAdmin: (userId: number, scopes: string[] = [], email?: string) =>
    apiRequest(`/admin/admins`, {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        email: email || null,
        scopes,
      }),
    }),

  patchAdmin: (
    adminUuid: string,
    body: { email?: string | null; scopes?: string[]; is_active?: boolean },
  ) =>
    apiRequest(`/admin/admins/${adminUuid}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  revokeAdmin: (adminUuid: string) =>
    apiRequest(`/admin/admins/${adminUuid}`, {
      method: 'DELETE',
    }),

  totpEnroll: () =>
    apiRequest(`/admin/totp/enroll`, { method: 'POST' }),

  totpVerify: (code: string) =>
    apiRequest(`/admin/totp/verify`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  totpReset: (adminUuid: string) =>
    apiRequest(`/admin/admins/${adminUuid}/totp`, { method: 'DELETE' }),

  // ── 4-eyes actions queue ────────────────────────────────────────────
  listActions: (statusFilter?: string, limit = 100) => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (statusFilter) p.set('status_filter', statusFilter);
    return apiRequest(`/admin/actions?${p.toString()}`);
  },

  createAction: (action_type: string, payload: Record<string, unknown>, totp_code: string) =>
    apiRequest(`/admin/actions`, {
      method: 'POST',
      body: JSON.stringify({ action_type, payload, totp_code }),
    }),

  approveAction: (actionId: string, totp_code: string) =>
    apiRequest(`/admin/actions/${actionId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ totp_code }),
    }),

  rejectAction: (actionId: string, totp_code: string, reason?: string) =>
    apiRequest(`/admin/actions/${actionId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ totp_code, reason: reason || null }),
    }),
};

export interface PendingAction {
  action_id: string;
  status: 'pending' | 'approved' | 'executed' | 'rejected' | 'expired';
  action_type: string;
  payload: Record<string, unknown>;
  created_by_admin_id: string;
  created_at: string;
  expires_at: string;
  approved_by_admin_id: string | null;
  approved_at: string | null;
  executed_at: string | null;
}

export interface PendingActionsList {
  items: PendingAction[];
  total: number;
}

export interface PcAdminUser {
  id: number;
  steam_id: string | null;
  steam_name: string | null;
  steam_avatar: string | null;
  steam_profile_url: string | null;
  last_activity: string | null;
}

export interface PcAdminItem {
  admin_id: string;
  external_id: string;
  email: string | null;
  is_active: boolean;
  scopes: string[];
  created_at: string;
  updated_at: string;
  totp_enrolled_at: string | null;
  /** Identity из монолита, резолвится по external_id=monolith:{id}. null если не найден. */
  user: PcAdminUser | null;
}

export interface TotpEnrollData {
  secret: string;       // otpauth://... URL
  qr_data_url: string;  // data:image/png;base64,...
  issuer: string;
  account_name: string;
}

export interface PcAdminListResponse {
  items: PcAdminItem[];
  total: number;
}

export interface MaintenanceState {
  enabled: boolean;
  message: string;
}

export interface AuditLogEntry {
  id: number;
  hash: string;
  prev_hash: string;
  payload: Record<string, unknown>;
}

export interface AuditLogPage {
  items: AuditLogEntry[];
  next_page_token: string | null;
}

export interface MarketItem {
  id: number;
  product_id: string;
  name: string;
  price_stars: number;
  price_usd: number;
  price_rub: number;
  price_rub_display: number;
  image: string;
  quantity: number;
  // Pre-computed on backend — no need to load 28MB JSON on client
  _rarity?: { name: string; color: string } | null;
  _category?: string | null;
  _stattrak?: boolean;
  /** Ручная выдача (delivery=1) — до 12 часов вместо ~минуты. */
  _slow_delivery?: boolean;
}
